import React from "react";
import { isEmpty } from "lodash";
import moment from "moment";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import Table from "examples/Tables/Table";
import GoalActionButton from "layouts/goals/components/GoalActionButton";
import GoalPill from "layouts/goals/components/GoalPill";
import {
  ASSETS_COLUMNS,
  END_REASON_LABELS,
  ASSETS_MESSAGES,
  formatMoney,
  personName,
  stateOf,
  typeOf,
} from "../assets.constants";

const muted = (text) => (
  <SuiTypography variant="caption" color="secondary">
    {text || "—"}
  </SuiTypography>
);

function AssetsTable({
  assets,
  onView,
  onEdit,
  onAssign,
  onReturn,
  onDispose,
  onDelete,
  isLoading,
}) {
  const rows = assets.map((asset) => {
    const type = typeOf(asset.type);
    const state = stateOf(asset.state);
    const holder = asset.current_assignment?.user;
    const disposed = asset.state === "disposed";
    return {
      asset: (
        <SuiBox display="flex" alignItems="center" px={1} py={0.5} gap={1.5}>
          <Icon color="secondary">{type?.icon || "devices_other"}</Icon>
          <SuiBox display="flex" flexDirection="column">
            <SuiTypography variant="button" fontWeight="medium">
              {asset.name}
            </SuiTypography>
            <SuiTypography variant="caption" color="secondary">
              {asset.serial_number || (asset.specs?.imeis || [])[0] || "No serial number"}
            </SuiTypography>
          </SuiBox>
        </SuiBox>
      ),
      "type / property of": (
        <SuiBox display="flex" flexDirection="column">
          {muted(type?.label)}
          <SuiTypography variant="caption" color="text">
            {asset.property_of}
          </SuiTypography>
        </SuiBox>
      ),
      "assigned to": disposed ? (
        <SuiBox display="flex" flexDirection="column">
          <SuiTypography variant="button" fontWeight="medium">
            {asset.disposed_to || "—"}
          </SuiTypography>
          <SuiTypography variant="caption" color="secondary">
            {[
              END_REASON_LABELS[asset.disposal_reason],
              asset.disposal_date && moment(asset.disposal_date).format("DD MMM YYYY"),
              asset.disposal_price !== null && formatMoney(asset.disposal_price, asset.currency),
            ]
              .filter(Boolean)
              .join(" · ")}
          </SuiTypography>
        </SuiBox>
      ) : holder ? (
        <SuiBox display="flex" flexDirection="column">
          <SuiTypography variant="button" fontWeight="medium">
            {personName(holder)}
          </SuiTypography>
          {asset.current_assignment && (
            <SuiTypography variant="caption" color="secondary">
              since {moment(asset.current_assignment.assigned_at).format("DD MMM YYYY")}
            </SuiTypography>
          )}
        </SuiBox>
      ) : (
        muted("—")
      ),
      status: <GoalPill color={state?.color}>{state?.label || asset.state}</GoalPill>,
      purchased: (
        <SuiBox display="flex" flexDirection="column">
          {muted(asset.purchase_date && moment(asset.purchase_date).format("DD MMM YYYY"))}
          {asset.price !== null && (
            <SuiTypography variant="caption" color="text">
              {formatMoney(asset.price, asset.currency)}
            </SuiTypography>
          )}
        </SuiBox>
      ),
      actions: (
        <SuiBox display="flex" gap={1} alignItems="center" justifyContent="center">
          {asset.state === "in_stock" && (
            <GoalActionButton
              icon="person_add"
              tooltip="Assign to employee"
              color="success"
              onClick={() => onAssign(asset)}
            />
          )}
          {asset.state === "assigned" && (
            <GoalActionButton
              icon="assignment_return"
              tooltip="Return to stock"
              color="info"
              onClick={() => onReturn(asset)}
            />
          )}
          {!disposed && (
            <GoalActionButton
              icon="archive"
              tooltip="Dispose (sold, severance, lost...)"
              color="warning"
              onClick={() => onDispose(asset)}
            />
          )}
          <GoalActionButton
            icon="visibility"
            tooltip="Details & history"
            color="secondary"
            onClick={() => onView(asset)}
          />
          <GoalActionButton
            icon="edit"
            tooltip="Edit asset"
            color="info"
            onClick={() => onEdit(asset)}
          />
          {isEmpty(asset.assignments) && (
            <GoalActionButton
              icon="delete"
              tooltip="Delete asset"
              color="error"
              onClick={() => onDelete(asset)}
            />
          )}
        </SuiBox>
      ),
    };
  });

  return (
    <Card>
      <SuiBox p={3}>
        {isLoading ? (
          <SuiBox display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress color="dark" size={30} />
          </SuiBox>
        ) : !isEmpty(rows) ? (
          <Table columns={ASSETS_COLUMNS} rows={rows} />
        ) : (
          <SuiBox
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <Icon fontSize="large" color="secondary" sx={{ opacity: 0.5 }}>
              devices
            </Icon>
            <SuiTypography variant="button" color="text" fontWeight="regular" mt={1}>
              {ASSETS_MESSAGES.empty}
            </SuiTypography>
          </SuiBox>
        )}
      </SuiBox>
    </Card>
  );
}

export default AssetsTable;
